import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { Instance } from 'mobx-state-tree';
import { inject } from 'mobx-react';
import * as React from 'react';
import { Button, ButtonGroup, Colors, InputGroup } from '@blueprintjs/core';
import { MAP, RESET } from '@blueprintjs/icons/lib/esm/generated/iconNames';
import { Page, TLCode } from '../../common/enums';
import BaseGridXL from '../common/grid/BaseGridXL';
import styled from '@emotion/styled';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import SimpleTextFormContent from '../common/form/SimpleTextFormGroup';
import EditCell from '../common/grid/EditCell';
import { Pharmacy } from '../../store/models/Pharmacy';
import { AuthenticationStore, SecretariatStore, UserStore } from '../../store';
import { Select } from 'antd';

interface PharmaciesListProps extends WithTranslateProps {
  deletePharmacy: (data: any) => void;
  filter: string;
  isLoading: boolean;
  navigate: (url: string) => void;
  pharmaciesList: Array<Instance<typeof Pharmacy>>;
  setFilterText: (filter: string) => void;
  showOnMap: () => void;
  reloadPharmacyList: () => void;
  activeFilter: string;
  levelFilter: string;
  groupFilter: string;
  secretariatFilter: number | undefined;
  setActiveFilter: (filter: string) => void;
  setLevelFilter: (filter: string) => void;
  setGroupFilter: (filter: string) => void;
  setTextFilter: (filter: string) => void;
  setSecretariatFilter: (filter: number | undefined) => void;
  UserStore?: Instance<typeof UserStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  SecretariatStore?: Instance<typeof SecretariatStore>;
}

interface PharmacyListState {
  currentGroupFilter: string;
}

export function renderStatus(params: ICellRendererParams) {
  const { value } = params;
  return value ? '' : 'non';
}

@inject('UserStore')
@inject('AuthenticationStore')
@inject('SecretariatStore')
class PharmaciesList extends React.Component<PharmaciesListProps, PharmacyListState> {
  state = {
    currentGroupFilter: '',
  };

  async componentDidMount() {
    if (this.props.UserStore!.dataList.length === 0) {
      await this.props.UserStore!.loadList();
    }
  }

  renderPharmacyUser = (params: ICellRendererParams) => {
    const user = this.props.UserStore!.dataList.find((user) => user.userName == params.data.apbNumber);
    return user ? user!.firstName! + ' ' + user!.lastName! : 'unknown';
  };

  private onRowDoubleClicked = (ev) => {
    this.props.navigate(`${Page.PharmacyPage}/${ev.data.pharmacyId}`);
  };

  private getColumnDefs = (): ColDef[] => {
    const { translate, navigate, deletePharmacy } = this.props;
    return [
      {
        headerName: '',
        field: 'level',
        width: 20,
        filter: false,
        sortable: true,
      },
      {
        headerName: translate(TLCode.PHARMACIES_ApbNum),
        field: 'apbNumber',
        width: 40,
      },
      {
        headerName: translate(TLCode.PHARMACIES_PharmacyName),
        field: 'name',
        resizable: true,
      },
      {
        headerName: translate(TLCode.PHARMACIES_Address),
        field: 'address',
        width: 160,
        resizable: true,
      },
      {
        headerName: translate(TLCode.PHARMACIES_ZipCode),
        field: 'zipCode',
        width: 31,
      },
      {
        headerName: translate(TLCode.PHARMACIES_City),
        field: 'city',
        width: 100,
      },
      {
        headerName: translate(TLCode.PHARAMCIES_Secretariat),
        field: 'secretariat',
        width: 80,
      },
      {
        headerName: translate(TLCode.PHARMACIES_Group),
        field: 'group',
        width: 40,
        resizable: true,
      },
      {
        headerName: 'Titulaire',
        field: 'userId',
        width: 90,
        cellRenderer: this.renderPharmacyUser,
      },
      {
        headerName: translate(TLCode.PHARMACIES_Active),
        field: 'status',
        width: 40,
        cellRendererFramework: renderStatus,
      },
      {
        headerName: '',
        width: 20,
        cellRendererFramework: EditCell,
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            onEditButtonClick: () => {
              navigate(`${Page.PharmacyPage}/${params.data.pharmacyId}`);
            },
          };
        },
      },
    ];
  };

  buildGridData = () => {
    const { activeFilter, levelFilter, groupFilter, secretariatFilter } = this.props;
    return this.props.pharmaciesList
      .filter((elem) => {
        let show = true;
        if (activeFilter === 'active' && !elem.isActive) {
          show = false;
        } else if (activeFilter === 'inactive' && elem.isActive) {
          show = false;
        } else if (levelFilter === 'three' && (elem.level == null || elem.level !== 3)) {
          show = false;
        } else if (levelFilter === 'one' && (elem.level == null || elem.level !== 1)) {
          show = false;
        } else if (levelFilter === 'zero' && (elem.level == null || elem.level !== 0)) {
          show = false;
        } else if (groupFilter != '' && !groupFilter.includes(elem.group!)) {
          show = false;
        } else if (
          secretariatFilter !== 0 &&
          secretariatFilter !== undefined &&
          elem.secretariatId !== secretariatFilter
        )
          show = false;

        return show;
      })
      .map((p) => ({
        pharmacyId: p.pharmacyId,
        apbNumber: p.apbNumber,
        name: p.name,
        phone: p.phone,
        address: p.address,
        zipCode: p.zipCode,
        city: p.city,
        level: p.level,
        status: p.isActive,
        zone: p.zoneId,
        density: p.density,
        anchor: p.anchor,
        group: p.group,
        secretariatId: p.secretariatId,
        secretariat:
          p.secretariatId === undefined
            ? ''
            : this.props.SecretariatStore!.dataList.find((s) => s.secretariatId === p.secretariatId)!.code,
      }));
  };

  handleClearGroupFilterClick = () => {
    this.props.setGroupFilter('');
    this.setState({ currentGroupFilter: '' });
  };

  renderStatus = (row) => {
    return row.data.status ? '' : 'non';
  };

  renderPharmacist = (row) => {
    const user = this.props.UserStore!.dataList.find((user) => user.userName == row.data.apbNumber);
    return user ? user!.firstName! + ' ' + user!.lastName! : 'unknown';
  };

  render() {
    const {
      setFilterText,
      filter,
      showOnMap,
      reloadPharmacyList,
      isLoading,
      setActiveFilter,
      setLevelFilter,
      setGroupFilter,
      setTextFilter,
      translate,
    } = this.props;

    const clearButton = <Button icon={'cross'} minimal={true} onClick={this.handleClearGroupFilterClick} />;
    const secretariatList = this.props.SecretariatStore!.dataList;

    return (
      <Container>
        <DivLine>
          <DivLeft style={{ width: '300px' }}>
            <div className="bp3-input-group .modifier" style={{ width: '300px' }}>
              <span className="bp3-icon bp3-icon-search"></span>
              <input
                type="text"
                className="bp3-input"
                placeholder="Search"
                value={filter}
                onChange={(e) => setTextFilter(e.target.value)}
                // onChange={(e) => setFilterText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.charCode == 13) {
                    const apbNr = parseInt(filter);
                    this.props.navigate(
                      `${Page.PharmacyPage}/${this.props.pharmaciesList[this.props.pharmaciesList.findIndex((ph) => ph.apbNumber == apbNr)].pharmacyId}`,
                    );
                  }
                  return false;
                }}
              ></input>
            </div>
          </DivLeft>
          <DivCenter style={{ width: '100%' }}>
            <SimpleTextFormContent label={TLCode.PHARMACIES_ActivityStatus}>
              <ButtonGroup>
                <Button
                  text={translate(TLCode.PHARMACIES_Active)}
                  onClick={() => setActiveFilter('active')}
                  active={this.props.activeFilter === 'active'}
                ></Button>
                <Button
                  text={translate(TLCode.PHARMACIES_Inactive)}
                  onClick={() => setActiveFilter('inactive')}
                  active={this.props.activeFilter === 'inactive'}
                ></Button>
                <Button
                  text={translate(TLCode.PHARMACIES_All_M)}
                  onClick={() => setActiveFilter('both')}
                  active={this.props.activeFilter === 'both'}
                ></Button>
              </ButtonGroup>
            </SimpleTextFormContent>
            <SimpleTextFormContent label={TLCode.PHARMACIES_Level}>
              <ButtonGroup>
                <Button
                  text={translate(TLCode.PHARMACIES_Three)}
                  onClick={() => setLevelFilter('three')}
                  active={this.props.levelFilter === 'three'}
                ></Button>
                <Button
                  text={translate(TLCode.PHARMACIES_One)}
                  onClick={() => setLevelFilter('one')}
                  active={this.props.levelFilter === 'one'}
                ></Button>
                <Button
                  text={translate(TLCode.PHARMACIES_Zero)}
                  onClick={() => setLevelFilter('zero')}
                  active={this.props.levelFilter === 'zero'}
                ></Button>
                <Button
                  text={translate(TLCode.PHARMACIES_All_M)}
                  onClick={() => setLevelFilter('all')}
                  active={this.props.levelFilter === 'all'}
                ></Button>
              </ButtonGroup>
            </SimpleTextFormContent>
            <SimpleTextFormContent label={TLCode.PHARMACIES_Groups}>
              <InputGroup
                placeholder="nom(s) séparés par ','"
                value={this.props.groupFilter}
                onChange={(e) => {
                  setGroupFilter(e.target.value);
                }}
                rightElement={clearButton}
              />
            </SimpleTextFormContent>
          </DivCenter>
          <DivRight>
            <Button
              icon={MAP}
              onClick={showOnMap}
              text={this.props.translate(TLCode.DutiesShowWideMap)}
              style={{ width: '170px' }}
            />
            <Button
              icon={RESET}
              onClick={reloadPharmacyList}
              text={this.props.translate(TLCode.PHARMACIES_LoadList)}
              style={{ width: '150px' }}
            />
          </DivRight>
        </DivLine>
        <DivLine>
          <DivCenter>
            <SimpleTextFormContent label={TLCode.PHARAMCIES_Secretariats}>
              <DivLine>
                <div style={{ width: '305px' }}>
                  <Select
                    defaultValue={
                      this.props.secretariatFilter === 0 ||
                      secretariatList === undefined ||
                      secretariatList.length === 0
                        ? undefined
                        : this.props.secretariatFilter === undefined
                        ? secretariatList.sort((s) => s.secretariatId)[0].code
                        : secretariatList.find((s) => s.secretariatId === this.props.secretariatFilter)!.code
                    }
                    style={{ width: 300 }}
                    onChange={(code) => {
                      const selectedSecretariatId =
                        code === 'All'
                          ? undefined
                          : (secretariatList.find((s) => s.code === code)!.secretariatId as number);
                      this.props.setSecretariatFilter(selectedSecretariatId);
                    }}
                  >
                    {secretariatList &&
                      secretariatList
                        .map((sec) => (
                          <Select.Option key={sec.code} value={sec.code}>
                            {sec.code}
                          </Select.Option>
                        ))
                        .concat(
                          <Select.Option key={'All'} value={'All'}>
                            {'All'}
                          </Select.Option>,
                        )}
                  </Select>
                </div>
              </DivLine>
            </SimpleTextFormContent>
          </DivCenter>
        </DivLine>
        <BaseGridXL
          keyName="pharmacyId"
          columnDefs={this.getColumnDefs()}
          rowData={this.buildGridData()}
          loading={isLoading}
          quickFilterText={filter}
          onRowDoubleClicked={this.onRowDoubleClicked}
          suppressClickEdit={true}
          getRowStyle={(params) => {
            if (
              params.data !== undefined &&
              this.props.AuthenticationStore!.isSecretariat &&
              (!params.data.secretariatId ||
                (params.data.secretariatId &&
                  params.data.secretariatId !== this.props.AuthenticationStore!.secretariatId))
            )
              return { 'background-color': Colors.LIGHT_GRAY1 };
            return { 'background-color': Colors.WHITE };
          }}
        />
      </Container>
    );
  }
}

export default withTranslate(PharmaciesList);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const DivLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 30%;
  margin-right: 5%;
  padding-bottom: 5px;
`;

const DivCenter = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 5px;
  .bp3-form-content > div {
    display: flex;
    flex-direction: row;
    margin-right: 20px;
    .bp3-control {
      margin-top: 0;
      margin-right: 5px;
    }
  }
  .bp3-form-group {
    margin-bottom: 0;
  }
`;
const DivRight = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 5px;
  .bp3-button {
    margin-right: 3px;
  }
`;
